import { useEffect, useState } from "react";

/* IS THIS A PHONE? — the ONE place the web app asks that question in JavaScript.
 *
 * ── WHY ANY JAVASCRIPT AT ALL, WHEN EVERY OTHER PHONE SHAPE IN THIS APP IS CSS ──────────────
 *
 * Because one of them is not a shape. The app's client screen shows ONE WEEK at a time, and the
 * number of weeks the calendar draws is not a property of a box — it is the `span` the component
 * builds its weeks from and the RANGE LABEL prints. A CSS rule can hide the second week; it
 * cannot stop the label saying `Mon 14 Sep – Sun 27 Sep` above one week of rows, which is a
 * sentence that is simply false. A layout can be faked from the outside; a claim cannot.
 *
 * ⚠️ **THE BREAKPOINT LIVES HERE ONCE AND THE STYLESHEET IS CHECKED AGAINST IT.** Two systems
 * enforcing one concept with two different numbers is this repo's own named trap — `WeeklyPlanner`
 * scoring day arrangements at a gap of 2 while the validator rejected at 3 — so `phoneWidth.test.ts`
 * asserts that `direction.css` contains this exact query text. If somebody moves the CSS band, the
 * ruler goes red rather than the two drifting apart in silence.
 *
 * ⚠️ **AND IT LISTENS.** A width read once in a `useState` initialiser is the 2026-09-18 bug one
 * shape over: these screens do not remount, so a phone rotated to landscape (or a desktop window
 * dragged narrow, which is how this gets looked at) would keep answering with the width it had at
 * mount. `change` on the `MediaQueryList` is the artefact; nothing polls.
 */

/** The one spelling. It must match `direction.css`'s band exactly — a ruler asserts that it does. */
export const PHONE_QUERY = "(width < 48rem)";

export function usePhoneWidth(): boolean {
  const [isPhone, setIsPhone] = useState<boolean>(() => matches());
  useEffect(() => {
    /* ⚠️ `matchMedia` CAN BE ABSENT — jsdom's default environment has no implementation, and a
       component test that mounted this would throw on render rather than fail a clause. The
       fallback is FALSE (the wide layout), because a test environment has no width worth
       believing and the desktop is the shape that has always been there. */
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(PHONE_QUERY);
    const onChange = () => { setIsPhone(mql.matches); };
    // Re-read on mount as well as on change: the first render may have happened before the
    // stylesheet resolved, and a stale `false` here is a two-week calendar on a phone.
    onChange();
    mql.addEventListener("change", onChange);
    return () => { mql.removeEventListener("change", onChange); };
  }, []);
  return isPhone;
}

function matches(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(PHONE_QUERY).matches;
}
